<template>
    <div class="quickpay-download-document">
        <!-- Desktop view -->
        <div class="quickpay-download-document__desktop">
            <p class="quickpay-download-document__title typographies-display-small">
                {{ translations.yourLabel }} {{ documentTypeLabel }}
            </p>
            <p
                class="quickpay-download-document__description typographies-paragraph-medium-regular"
            >
                {{ translations.description }}
            </p>
            <CompassButton
                :label="translations.downloadDocument"
                :type="CompassEnums.ButtonType.TERTIARY"
                leftIcon="download"
                :loading="isDownloading"
                @click="downloadDocument"
            />
        </div>

        <!-- Mobile view -->
        <div class="quickpay-download-document__mobile">
            <div class="quickpay-download-document__mobile-preview">
                <img
                    src="/ressources/logged/img/illustrations/payment/pdf-preview-mobile.svg"
                    alt="PDF Preview"
                    class="quickpay-download-document__mobile-preview-image"
                />
                <div v-if="expired" class="quickpay-download-document__mobile-preview-expired">
                    <span class="quickpay-download-document__mobile-preview-expired-text">
                        {{ translations.expired }}
                    </span>
                </div>
            </div>
            <div class="quickpay-download-document__mobile-content">
                <div class="quickpay-download-document__mobile-header">
                    <div class="quickpay-download-document__mobile-info">
                        <p
                            v-if="documentTypeLabel"
                            class="quickpay-download-document__mobile-type typographies-paragraph-large-bold"
                        >
                            {{ documentTypeLabel }}
                        </p>
                        <p
                            v-if="documentNumber"
                            class="quickpay-download-document__mobile-number typographies-paragraph-medium-regular"
                        >
                            N° {{ documentNumber }}
                        </p>
                    </div>
                    <TagStatus
                        v-if="statusData"
                        :id="statusData.id"
                        :label="statusData.label"
                        size="small"
                        class="quickpay-download-document__mobile-tag"
                    />
                </div>
                <CompassPlainButton
                    :label="translations.download"
                    :variant="CompassEnums.PlainButtonVariantType.NEUTRAL"
                    leftIcon="download"
                    :loading="isDownloading"
                    class="quickpay-download-document__mobile-button"
                    @click="downloadDocument"
                />
            </div>
        </div>
    </div>
</template>

<script>
import TagStatus from 'vcomponents/common/TagStatus/TagStatus.vue';
import SalesDocumentStatus from 'Config/authenticated/SalesDocumentsStatus';
import docTypeLabel from 'Dictionaries/docTypeLabel';
import { trackDownload } from 'api/quickpay/quickpayTrackingService.js';

export default {
    components: {
        TagStatus,
    },
    props: {
        docType: {
            type: String,
            required: true,
        },
        docId: {
            type: Number,
            required: true,
        },
        corpId: {
            type: Number,
            required: true,
        },
        pdfUrl: {
            type: String,
            required: true,
        },
        documentNumber: {
            type: String,
            default: '',
        },
        documentStep: {
            type: String,
            default: '',
        },
        expired: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isDownloading: false,
            translations: {
                yourLabel: this.$t.__('common.labels.your'),
                download: this.$t.__('common.labels.download'),
                downloadDocument: this.$t.__('common.labels.download_document'),
                description: this.$t.__('quickpay.QuickpayDownloadDocument.overview.description'),
                expired: this.$t.__('common.labels.expired').toUpperCase(),
            },
        };
    },
    computed: {
        documentTypeLabel() {
            return docTypeLabel[this.docType]?.label || '';
        },
        statusData() {
            if (!this.docType || !this.documentStep) {
                return null;
            }
            return {
                id: this.documentStep,
                label: SalesDocumentStatus[this.docType][this.documentStep]?.shortLabel,
            };
        },
        fileName() {
            return this.documentNumber
                ? `${this.documentTypeLabel}_${this.documentNumber}.pdf`
                : `${this.documentTypeLabel}.pdf`;
        },
    },
    methods: {
        async downloadDocument() {
            if (this.isDownloading) {
                return;
            }
            this.isDownloading = true;
            try {
                const response = await fetch(this.pdfUrl);
                if (!response.ok) {
                    throw new Error('Erreur de téléchargement');
                }
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = this.fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                trackDownload(this.docType, this.docId, this.corpId);
            } finally {
                this.isDownloading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.quickpay-download-document {
    width: 100%;
    &__desktop {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-3);

        @media (max-width: 1023px) {
            display: none;
        }
    }
    &__title {
        margin: 0;
        color: var(--colors-common-text-brand-secondary-blue);
    }
    &__description {
        max-width: 300px;
        text-align: center;
        margin: 0;
        color: var(--colors-common-text-standard-caption);
    }
    &__mobile {
        display: none;
        background: var(--colors-common-background-default);
        border-radius: var(--spacing-1);
        padding: var(--spacing-4);
        box-shadow: var(--elevation-1);
        width: 100%;
        gap: var(--spacing-4);

        @media (max-width: 1023px) {
            display: flex;
        }

        &-preview {
            width: 100px;
            border-radius: var(--spacing-1);
            overflow: hidden;
            box-shadow: var(--elevation-1);
            background: var(--colors-common-background-default);
            flex-shrink: 0;
            position: relative;
            align-self: stretch;
        }
        &-preview-expired {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
            z-index: 10;
        }
        &-preview-expired-text {
            color: rgba(138, 138, 138, 0.4);
            font-size: 18px;
            font-weight: 700;
            text-align: center;
            text-transform: uppercase;
            transform: rotate(-45deg);
            user-select: none;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: clip;
            max-width: 100%;
        }
        &-preview-image {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
        }
        &-content {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-2);
            min-width: 0;
            flex: 1;
        }
        &-header {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-2);
            flex: 1;
        }
        &-info {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
            min-width: 0;
        }
        &-type {
            color: var(--colors-common-text-standard-default);
            margin: 0;
        }
        &-number {
            color: var(--colors-common-text-standard-default);
            margin: 0;
        }
        &-tag {
            align-self: flex-start;
        }
        &-button {
            display: flex;
        }
    }
}
</style>
